import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Logo } from "../components/Logo";
import { ThemeToggle } from "../components/ThemeToggle";
import { AmbientBlobs } from "../components/motion/AmbientBlobs";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowLeft, ArrowRight, Gift } from "lucide-react";

const RegisterPage = () => {
  const [searchParams] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) setReferralCode(ref.toUpperCase());
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await register(email, password, name, referralCode || null);
      if (result?.referral_bonus) {
        toast.success(`Account created! +${result.referral_bonus} bonus tokens from your referral.`);
      } else {
        toast.success("Account created. Welcome to XEA.");
      }
      navigate("/dashboard");
    } catch (error) {
      const msg = error.response?.data?.detail || "Registration failed. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink-50 flex">
      {/* Left — ambient */}
      <div className="hidden lg:block lg:flex-1 relative bg-ink-950 overflow-hidden">
        <AmbientBlobs />
        <div className="absolute inset-0 flex items-center justify-center p-16">
          <div className="relative max-w-md">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-citrus-500">Join XEA</p>
            <h2 className="font-display font-black text-white text-4xl tracking-[-0.03em] leading-tight mt-3">
              Get 25 free tokens. <br /> Generate your first creative in under a minute.
            </h2>
            <p className="mt-4 text-white/60 font-light">
              No credit card. Cancel anytime.
            </p>
          </div>
        </div>
      </div>

      {/* Right — form */}
      <div className="flex-1 flex flex-col px-6 lg:px-16 xl:px-24 py-10">
        <div className="flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-medium text-ink-500 hover:text-ink-950 w-fit"
          >
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
          <ThemeToggle />
        </div>

        <div className="flex-1 flex flex-col justify-center max-w-sm w-full mx-auto">
          <Logo size={40} tagline animated linkTo={null} className="mb-10" />

          <h1 className="font-display font-black text-4xl tracking-[-0.04em] leading-tight">
            Create your account.
          </h1>
          <p className="text-ink-500 mt-2 font-light">Join XEA in 30 seconds.</p>

          {referralCode && (
            <div className="mt-6 flex items-center gap-3 p-4 rounded-2xl bg-citrus-500/15 border border-citrus-500/40">
              <Gift className="w-4 h-4 text-ink-950 shrink-0" />
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-ink-950">Referral applied</p>
                <p className="text-sm text-ink-700">Code <span className="font-bold">{referralCode}</span> — bonus tokens incoming.</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5 mt-8">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-xs font-bold uppercase tracking-[0.18em] text-ink-500">
                Name
              </Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Alex Founder"
                required
                data-testid="register-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-bold uppercase tracking-[0.18em] text-ink-500">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                data-testid="register-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-bold uppercase tracking-[0.18em] text-ink-500">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  required
                  className="pr-12"
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-ink-500 hover:text-ink-950"
                  aria-label="toggle password visibility"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {!referralCode && (
              <div className="space-y-2">
                <Label htmlFor="ref" className="text-xs font-bold uppercase tracking-[0.18em] text-ink-500">
                  Referral code <span className="text-ink-300 normal-case tracking-normal font-medium">(optional)</span>
                </Label>
                <Input
                  id="ref"
                  type="text"
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                  placeholder="XEA-XXXX"
                  data-testid="register-referral-input"
                />
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              size="lg"
              className="w-full"
              data-testid="register-submit-btn"
            >
              {loading ? "Creating account..." : (<>Create account <ArrowRight className="w-4 h-4" /></>)}
            </Button>
          </form>

          <p className="mt-6 text-sm text-ink-500">
            Already have an account?{" "}
            <Link to="/login" className="text-ink-950 font-semibold hover:underline underline-offset-4">
              Sign in
            </Link>
          </p>

          <p className="mt-6 text-[11px] text-ink-500 leading-relaxed">
            By creating an account you agree to our{" "}
            <Link to="/terms" className="text-ink-950 hover:underline">Terms</Link> and{" "}
            <Link to="/privacy" className="text-ink-950 hover:underline">Privacy Policy</Link>.
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
